import { SkeletonBox } from "@ui/components/Skeleton";
import {
  Children,
  createContext,
  type ElementType,
  isValidElement,
  type ReactElement,
  type ReactNode,
  useContext,
} from "react";
import { type HeadingColor, type HeadingDivider, type HeadingSize, type HeadingWeight, heading } from "./styles";

interface HeadingContextValue {
  styles: ReturnType<typeof heading>;
}

const HeadingContext = createContext<HeadingContextValue | null>(null);

function useHeadingContext(slot: string) {
  const ctx = useContext(HeadingContext);
  if (!ctx) throw new Error(`Heading.${slot} must be used within Heading.Root`);
  return ctx;
}

function findSlot(children: ReactNode, slotType: unknown): ReactElement | undefined {
  return Children.toArray(children).find(
    (child): child is ReactElement => isValidElement(child) && child.type === slotType,
  );
}

interface HeadingRootProps {
  children: ReactNode;
  className?: string;
  color?: HeadingColor;
  divider?: HeadingDivider;
  size?: HeadingSize;
  weight?: HeadingWeight;
}

function HeadingRoot({ children, className, color, divider, size, weight }: HeadingRootProps) {
  const titleChild = findSlot(children, HeadingTitle);
  const subtitleChild = findSlot(children, HeadingSubtitle);
  const actionsChild = findSlot(children, HeadingActions);

  const styles = heading({ color, divider, size, weight });

  return (
    <HeadingContext value={{ styles }}>
      <div className={styles.root({ className })}>
        <div className={styles.bar()}>
          <div className={styles.leftLine()} />
          {titleChild}
          <div className={styles.spacer()}>
            <div className={styles.rightLine()} />
            {actionsChild}
          </div>
        </div>
        {subtitleChild}
      </div>
    </HeadingContext>
  );
}

interface HeadingTitleProps {
  as?: ElementType;
  children: ReactNode;
}

function HeadingTitle({ as: Tag = "h2", children }: HeadingTitleProps) {
  const { styles } = useHeadingContext("Title");
  return <Tag className={styles.title()}>{children}</Tag>;
}

interface HeadingSubtitleProps {
  children: ReactNode;
}

function HeadingSubtitle({ children }: HeadingSubtitleProps) {
  const { styles } = useHeadingContext("Subtitle");
  return <p className={styles.subtitle()}>{children}</p>;
}

interface HeadingActionsProps {
  children: ReactNode;
}

function HeadingActions({ children }: HeadingActionsProps) {
  const { styles } = useHeadingContext("Actions");
  return <div className={styles.actions()}>{children}</div>;
}

interface HeadingSkeletonProps {
  withAction?: boolean;
}

function HeadingSkeleton({ withAction }: HeadingSkeletonProps) {
  const styles = heading({ divider: "responsive" });
  return (
    <div className={styles.bar()}>
      <div className={styles.leftLine()} />
      <SkeletonBox className="h-7 w-24 shrink-0" />
      <div className={styles.spacer()}>
        <div className={styles.rightLine()} />
        {withAction && <SkeletonBox className="hidden h-10 w-24 shrink-0 md:block" rounded="full" />}
      </div>
    </div>
  );
}

export const Heading = {
  Root: HeadingRoot,
  Title: HeadingTitle,
  Subtitle: HeadingSubtitle,
  Actions: HeadingActions,
  Skeleton: HeadingSkeleton,
};
